import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'
import type { User } from 'firebase/auth'
import { getLegalConsentRepository } from '@/repositories'
import {
  callAcceptLegalTerms,
  getLegalTermsErrorCode,
} from '@/services/firebase/legalTerms.service'
import { logout as firebaseLogout } from '@/services/firebase/auth.service'
import { userService } from '@/services/userService'
import type { CurrentLegalVersion } from '@/types'

/**
 * Consentimiento de los Términos de uso y la Política de privacidad
 * (ADR-SEC-013 local — HU-AUTH-006, TRX-007).
 *
 * - Decide, al iniciar o restaurar sesión, si el usuario debe aceptar: lee
 *   `users/{uid}` y `legaldocs_current_version/current` del SERVIDOR.
 * - Gobierna el modal único montado en `App.vue` (`LegalConsentModal`).
 * - Nunca escribe el consentimiento: lo registra la Cloud Function
 *   `acceptLegalTerms` (único escritor). Sólo aprovisiona `users/{uid}` en el
 *   modo `google-new`, DESPUÉS de que el usuario marcó la casilla.
 * - El resultado se recuerda por `uid` durante la sesión: el guard no relee en
 *   cada navegación.
 */

export type LegalConsentMode = 'first' | 'update' | 'google-new'
export type LegalConsentOutcome = 'ok' | 'exited'

export const CONSENT_GENERIC_ERROR = 'No pudimos registrar tu aceptación. Intenta de nuevo.'
export const CONSENT_OUTDATED_NOTICE =
  'Los términos se actualizaron mientras los revisabas. Revísalos y vuelve a aceptarlos.'

export interface ConsentAccount {
  name: string | null
  email: string | null
  photoUrl: string | null
  /** La cuenta entró con Google: el modal «Un último paso» muestra su tarjeta. */
  isGoogle: boolean
}

type Evaluation = 'ok' | 'required'

export const useLegalConsentStore = defineStore('legalConsent', () => {
  const required = ref(false)
  const mode = ref<LegalConsentMode | null>(null)
  const current = ref<CurrentLegalVersion | null>(null)
  const displayName = ref<string | null>(null)
  const account = ref<ConsentAccount | null>(null)
  const submitting = ref(false)
  const error = ref<string | null>(null)
  // Sube cada vez que el modal debe desmarcar su casilla (versión cambiada).
  const revision = ref(0)
  // uid cuyo consentimiento quedó resuelto en esta sesión.
  const settledUid = ref<string | null>(null)
  // Usuario de Firebase al que se le pide el consentimiento. `shallowRef`: objeto del SDK.
  const subject = shallowRef<User | null>(null)

  // Estado no reactivo de la orquestación.
  let acceptedVersion: string | null = null
  let pendingFlow: Promise<void> | null = null
  let evaluation: { uid: string; promise: Promise<Evaluation> } | null = null
  let decision: {
    promise: Promise<LegalConsentOutcome>
    resolve: (outcome: LegalConsentOutcome) => void
  } | null = null

  /**
   * Marca un flujo de registro en curso (correo o Google con la casilla): el
   * guard espera a que termine antes de evaluar, para no pedir el
   * consentimiento que el propio registro está registrando.
   */
  function beginFlow(): () => void {
    let end: () => void = () => {}
    const flow = new Promise<void>((resolve) => {
      end = resolve
    })
    pendingFlow = flow
    return () => {
      if (pendingFlow === flow) pendingFlow = null
      end()
    }
  }

  function settle(uid: string) {
    settledUid.value = uid
    required.value = false
    mode.value = null
    error.value = null
    subject.value = null
  }

  function resolveDecision(outcome: LegalConsentOutcome) {
    const pending = decision
    decision = null
    pending?.resolve(outcome)
  }

  async function runEvaluation(user: User): Promise<Evaluation> {
    const repo = getLegalConsentRepository()
    let cur: CurrentLegalVersion | null
    let state: Awaited<ReturnType<typeof repo.getUserLegalState>>
    try {
      ;[cur, state] = await Promise.all([repo.getCurrentVersion(), repo.getUserLegalState(user.uid)])
    } catch (err) {
      // Sin servidor no se decide: se deja pasar y se reevalúa en la próxima navegación.
      console.error('[consentimiento] No se pudo leer el estado de consentimiento:', err)
      return 'ok'
    }

    if (!cur) {
      // Decisión del owner (fail-open): no bloquear por un documento ausente.
      console.error(
        '[consentimiento] Falta legaldocs_current_version/current: se deja pasar sin pedir consentimiento.'
      )
      settle(user.uid)
      return 'ok'
    }

    current.value = cur
    acceptedVersion = state.acceptedVersion
    if (state.exists && state.acceptedVersion === cur.version_no) {
      settle(user.uid)
      return 'ok'
    }

    subject.value = user
    displayName.value = state.displayName || user.displayName || null
    account.value = {
      name: user.displayName ?? null,
      email: user.email ?? null,
      photoUrl: user.photoURL ?? null,
      isGoogle: user.providerData.some((p) => p.providerId === 'google.com'),
    }
    mode.value = !state.exists ? 'google-new' : state.acceptedVersion === null ? 'first' : 'update'
    error.value = null
    required.value = true
    return 'required'
  }

  /** `'ok'` si el usuario puede seguir; `'required'` si el modal debe pedir la aceptación. */
  async function evaluate(user: User): Promise<Evaluation> {
    if (pendingFlow) await pendingFlow
    if (settledUid.value === user.uid) return 'ok'
    if (required.value && subject.value?.uid === user.uid) return 'required'
    if (evaluation && evaluation.uid === user.uid) return evaluation.promise

    const promise = runEvaluation(user)
    evaluation = { uid: user.uid, promise }
    try {
      return await promise
    } finally {
      if (evaluation?.promise === promise) evaluation = null
    }
  }

  function waitForDecision(): Promise<LegalConsentOutcome> {
    if (!decision) {
      let resolve: (outcome: LegalConsentOutcome) => void = () => {}
      const promise = new Promise<LegalConsentOutcome>((r) => {
        resolve = r
      })
      decision = { promise, resolve }
    }
    return decision.promise
  }

  /**
   * Punto único que usan el guard y `redirectAfterAuth` (TRX-006): evalúa y, si
   * hace falta, espera la decisión del usuario en el modal. `'exited'` ⇒ la
   * sesión ya se cerró.
   */
  async function ensureConsent(user: User): Promise<LegalConsentOutcome> {
    const result = await evaluate(user)
    if (result === 'ok') return 'ok'
    return waitForDecision()
  }

  async function refreshAfterOutdated() {
    try {
      const cur = await getLegalConsentRepository().getCurrentVersion()
      if (cur) {
        current.value = cur
        if (mode.value !== 'google-new') mode.value = acceptedVersion === null ? 'first' : 'update'
      }
      error.value = CONSENT_OUTDATED_NOTICE
    } catch (err) {
      console.error('[consentimiento] No se pudo releer la versión vigente:', err)
      error.value = CONSENT_GENERIC_ERROR
    }
    revision.value++
  }

  /** «Continuar» del modal. */
  async function accept() {
    const user = subject.value
    const currentMode = mode.value
    const cur = current.value
    if (!user || !currentMode || !cur || submitting.value) return

    submitting.value = true
    error.value = null
    try {
      if (currentMode === 'google-new') {
        // Aprovisionar SÓLO ahora: la casilla ya está marcada (HU-AUTH-006).
        await userService.ensureUserDocument(user, 'student')
      }
      await callAcceptLegalTerms(
        cur.version_no,
        currentMode === 'google-new' ? 'signup_google_modal' : 'gate_modal'
      )
      settle(user.uid)
      resolveDecision('ok')
    } catch (err) {
      const code = getLegalTermsErrorCode(err)
      if (code === 'TERMS_VERSION_OUTDATED') {
        await refreshAfterOutdated()
      } else if (code === 'LEGAL_VERSION_MISSING') {
        console.error(
          '[consentimiento] La función no encontró la versión vigente: se deja pasar (fail-open).'
        )
        settle(user.uid)
        resolveDecision('ok')
      } else {
        console.error('[consentimiento] acceptLegalTerms falló:', err)
        error.value = CONSENT_GENERIC_ERROR
      }
    } finally {
      submitting.value = false
    }
  }

  function reset() {
    required.value = false
    mode.value = null
    current.value = null
    displayName.value = null
    account.value = null
    error.value = null
    subject.value = null
    settledUid.value = null
    acceptedVersion = null
    evaluation = null
    resolveDecision('exited')
  }

  /** «Salir» / «Cancelar»: cierra la sesión. No hay forma de seguir sin aceptar. */
  async function exit() {
    submitting.value = true
    try {
      await firebaseLogout()
    } catch (err) {
      console.error('[consentimiento] No se pudo cerrar la sesión:', err)
    } finally {
      submitting.value = false
      reset()
    }
  }

  /**
   * Registro con la casilla marcada (canal `signup_checkbox`). Best-effort: si
   * falla, NO bloquea el registro — el guard pedirá el consentimiento en la
   * siguiente evaluación. Devuelve `true` si quedó registrado.
   */
  async function acceptAtSignup(uid: string): Promise<boolean> {
    try {
      const cur = await getLegalConsentRepository().getCurrentVersion()
      if (!cur) {
        console.error(
          '[consentimiento] Falta legaldocs_current_version/current: el registro sigue sin registrar la aceptación.'
        )
        return false
      }
      await callAcceptLegalTerms(cur.version_no, 'signup_checkbox')
      settle(uid)
      return true
    } catch (err) {
      console.warn('[consentimiento] No se registró la aceptación del registro; se pedirá al entrar:', err)
      return false
    }
  }

  return {
    required,
    mode,
    current,
    displayName,
    account,
    submitting,
    error,
    revision,
    settledUid,
    beginFlow,
    evaluate,
    ensureConsent,
    accept,
    exit,
    reset,
    acceptAtSignup,
  }
})
